import React, {useEffect, useState} from 'react';
import {
    Box,
    Textarea,
    HStack,
    Button,
    IconButton,
    useColorModeValue,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Text,
    Switch,
    Tooltip,
} from '@chakra-ui/react';
import { FiSend, FiChevronDown, FiSettings, FiFolder, FiGlobe, FiCpu, FiUpload, FiFile, FiPlus } from 'react-icons/fi';
import { FaArrowUp } from "react-icons/fa6";

/**
 * EnhancedTextareaInput - A modern, feature-rich input component
 * 
 * @param {Object} props
 * @param {React.Ref} props.inputRef - Ref for the textarea element
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Handler for input changes
 * @param {Function} props.onKeyDown - Handler for key down events
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isDisabled - Whether the submit button is disabled
 * @param {boolean} props.isStreaming - Whether currently streaming a response
 * @param {Function} props.onFocus - Handler for focus event
 * @param {Function} props.onBlur - Handler for blur event
 * @param {React.ReactNode} props.leftButtons - Custom buttons to show on the left side
 * @param {boolean} props.showMentionButton - Whether to show the mention button
 * @param {Function} props.onMentionClick - Handler for mention button click
 * @param {boolean} props.showModelSelector - Whether to show model selector
 * @param {string} props.selectedModel - Currently selected model
 * @param {Function} props.onModelChange - Handler for model change
 * @param {boolean} props.showLimitSelector - Whether to show limit selector
 * @param {string} props.selectedLimit - Currently selected limit
 * @param {Function} props.onLimitChange - Handler for limit change
 * @param {boolean} props.showSettings - Whether to show settings menu
 * @param {Object} props.settingsState - State object for settings (workspace, internet, modelKnowledge)
 * @param {Function} props.onSettingsChange - Handler for settings changes
 * @param {boolean} props.showUploadMenu - Whether to show upload menu
 * @param {Function} props.onUploadFile - Handler for file upload
 * @param {Function} props.onCreateNote - Handler for create note
 */
export const EnhancedTextareaInput = ({
    inputRef,
    value,
    onChange,
    onKeyDown,
    onSubmit,
    placeholder = "Ask me anything...",
    isDisabled = false,
    isStreaming = false,
    onFocus,
    onBlur,
    leftButtons,
    showMentionButton = false,
    onMentionClick,
    showModelSelector = true,
    selectedModel = "GPT 5",
    onModelChange,
    showLimitSelector = true,
    selectedLimit = "Default citations",
    onLimitChange,
    showSettings = true,
    settingsState = {
        workspace: true,
        internet: true,
        modelKnowledge: true,
        largeContext: false
    },
    onSettingsChange,
    showUploadMenu = true,
    onUploadFile,
    onCreateNote,
}) => {
    const [isInputFocused, setIsInputFocused] = useState(false);
    
    const handleFocus = (e) => {
        setIsInputFocused(true);
        onFocus?.(e);
    };
    
    const handleBlur = (e) => {
        setIsInputFocused(false);
        onBlur?.(e);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isDisabled && !isStreaming && value.trim()) {
            onSubmit?.(e);
        }
    };

    // i added this useEffect
    useEffect(() => {
        if (!value && inputRef.current) {
            inputRef.current.style.height = 'auto';
        }
    }, [value, inputRef]);

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box
                bg={useColorModeValue('white', 'gray.700')}
                borderWidth="1px"
                borderColor={isInputFocused 
                    ? 'blue.400'
                    : 'gray.200'
                }
                borderRadius="xl"
                pt={4}
                px={4}
                pb={2.5}
                position="relative"
                boxShadow={useColorModeValue(
                    '0 4px 12px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.08)',
                    '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)'
                )}
                _hover={{
                    borderColor: isInputFocused 
                        ? 'blue.400'
                        : 'gray.300',
                    boxShadow: useColorModeValue(
                        '0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.12)',
                        '0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15)'
                    )
                }}
                transition="all 0.2s ease-in-out"
            >
                {/* Top Row - Text Input */}
                <Textarea
                    ref={inputRef}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onInput={(e) => {
                        // Auto-expand as text overflows
                        e.currentTarget.style.height = 'auto';
                        e.currentTarget.style.height = (e.currentTarget.scrollHeight) + 'px';
                    }}
                    placeholder={placeholder}
                    fontSize="md"
                    fontWeight="400"
                    color={useColorModeValue('black', 'white')}
                    bg="transparent"
                    border="none"
                    p={0}
                    minH="24px"
                    maxH="420px"
                    resize="none"
                    rows={1}
                    _focus={{
                        boxShadow: 'none',
                        outline: 'none'
                    }}
                    _placeholder={{
                        color: useColorModeValue('gray.400', 'gray.500')
                    }}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: useColorModeValue('gray.300', 'gray.600'),
                            borderRadius: '2px',
                        },
                    }}
                    mb={3}
                />
                
                {/* Bottom Row - Buttons */}
                <HStack justify="space-between" align="center" mt={1}>
                    <HStack spacing={2}>
                        {/* Custom left buttons if provided */}
                        {leftButtons}
                        
                        {/* Mention Button */}
                        {showMentionButton && (
                            <Button
                                size="sm"
                                bg={'blue.50'}
                                color={'black'}
                                fontWeight="500"
                                fontSize="sm"
                                h="28px"
                                px={4}
                                borderRadius="md"
                                leftIcon={<Text fontSize="sm" color="blue.600">@</Text>}
                                _hover={{
                                    bg: 'blue.100'
                                }}
                                onClick={onMentionClick}
                            >
                                Mention
                            </Button>
                        )}
                        
                        {/* Model Selector */}
                        {showModelSelector && (
                            <Menu placement="bottom-start">
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    bg={'gray.100'}
                                    color={'black'}
                                    fontWeight="500"
                                    fontSize="sm"
                                    h="28px"
                                    px={4}
                                    borderRadius="md"
                                    leftIcon={<Box w={2.5} h={2.5} bg="gray.500" borderRadius="sm" />}
                                    rightIcon={<FiChevronDown size={12} color="gray.500" />}
                                    _hover={{
                                        bg: 'gray.200'
                                    }}
                                    _active={{
                                        bg: 'gray.200'
                                    }}
                                >
                                    {selectedModel}
                                </MenuButton>
                                <MenuList
                                    bg={'white'}
                                    border="1px"
                                    borderColor={'gray.200'}
                                    borderRadius="md"
                                    boxShadow="lg"
                                    py={1}
                                    minW="160px"
                                >
                                    {['GPT-4o', 'GPT-4', 'Claude 3.5 Sonnet', 'Gemini Pro'].map((model) => (
                                        <MenuItem
                                            key={model}
                                            fontSize="sm"
                                            py={2}
                                            px={3}
                                            onClick={() => onModelChange?.(model)}
                                            _hover={{
                                                bg: 'gray.50'
                                            }}
                                        >
                                            {model}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        )}
                        
                        {/* Limit Selector */}
                        {showLimitSelector && (
                            <Menu placement="bottom-start">
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    bg={'gray.100'}
                                    color={'black'}
                                    fontWeight="500"
                                    fontSize="sm"
                                    h="28px"
                                    pr={2}
                                    pl={3}
                                    borderRadius="md"
                                    rightIcon={<FiChevronDown size={12} color="gray.500" />}
                                    _hover={{
                                        bg: 'gray.200'
                                    }}
                                    _active={{
                                        bg: 'gray.200'
                                    }}
                                >
                                    {selectedLimit}
                                </MenuButton>
                                <MenuList
                                    bg={'white'}
                                    border="1px"
                                    borderColor={'gray.200'}
                                    borderRadius="md"
                                    boxShadow="lg"
                                    py={1}
                                    minW="140px"
                                >
                                    {[
                                        { value: 'Default citations', tooltip: 'Our normal clickable in-text citations [filename, page #]' },
                                        { value: 'No citations', tooltip: 'No in-text citations at all' },
                                        // { value: 'MLA', tooltip: 'MLA format (Author page)' },
                                        // { value: 'APA', tooltip: 'APA format (Author, Year)' },
                                        // { value: 'Chicago', tooltip: 'Chicago format (Author Year)' }
                                    ].map((option) => (
                                        <Tooltip
                                            key={option.value}
                                            label={option.tooltip}
                                            placement="left"
                                            hasArrow
                                            bg={'gray.700'}
                                            color="white"
                                            fontSize="xs"
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                        >
                                            <MenuItem
                                                fontSize="sm"
                                                py={2}
                                                px={3}
                                                onClick={() => onLimitChange?.(option.value)}
                                                _hover={{
                                                    bg: 'gray.50'
                                                }}
                                            >
                                                {option.value}
                                            </MenuItem>
                                        </Tooltip>
                                    ))}
                                </MenuList>
                            </Menu>
                        )}
                        
                        {/* Settings Menu */}
                        {showSettings && (
                            <Menu placement="bottom-start">
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    bg={'gray.100'}
                                    color={'black'}
                                    fontWeight="500"
                                    fontSize="sm"
                                    h="28px"
                                    px={4}
                                    borderRadius="md"
                                    _hover={{
                                        bg: 'gray.200'
                                    }}
                                    _active={{
                                        bg: 'gray.200'
                                    }}
                                >
                                    <FiSettings size={14} />
                                </MenuButton>
                                <MenuList
                                    bg={'white'}
                                    border="1px"
                                    borderColor={'gray.200'}
                                    borderRadius="md"
                                    boxShadow="lg"
                                    py={2}
                                    minW="180px"
                                >
                                    {/*<Box */}
                                    {/*    px={3} */}
                                    {/*    py={2}*/}
                                    {/*    cursor="pointer"*/}
                                    {/*    _hover={{*/}
                                    {/*        bg: useColorModeValue('gray.50', 'gray.700')*/}
                                    {/*    }}*/}
                                    {/*    onClick={() => onSettingsChange?.('workspace', !settingsState.workspace)}*/}
                                    {/*>*/}
                                    {/*    <HStack justify="space-between" align="center">*/}
                                    {/*        <HStack spacing={2}>*/}
                                    {/*            <FiFolder size={14} color="gray.600" />*/}
                                    {/*            <Text fontSize="sm" fontWeight="500">Workspace</Text>*/}
                                    {/*        </HStack>*/}
                                    {/*        <Switch*/}
                                    {/*            size="sm"*/}
                                    {/*            colorScheme="blue"*/}
                                    {/*            isChecked={settingsState.workspace}*/}
                                    {/*            onChange={() => {}}*/}
                                    {/*            pointerEvents="none"*/}
                                    {/*        />*/}
                                    {/*    </HStack>*/}
                                    {/*</Box>*/}
                                    <Box 
                                        px={3} 
                                        py={2}
                                        cursor="pointer"
                                        _hover={{
                                            bg: 'gray.50'
                                        }}
                                        onClick={() => onSettingsChange?.('internet', !settingsState.internet)}
                                    >
                                        <HStack justify="space-between" align="center">
                                            <HStack spacing={2}>
                                                <FiGlobe size={14} color="gray.600" />
                                                <Text fontSize="sm" fontWeight="500">Internet</Text>
                                            </HStack>
                                            <Switch
                                                size="sm"
                                                colorScheme="blue"
                                                isChecked={settingsState.internet}
                                                onChange={() => {}}
                                                pointerEvents="none"
                                            />
                                        </HStack>
                                    </Box>
                                    {/*<Box */}
                                    {/*    px={3} */}
                                    {/*    py={2}*/}
                                    {/*    cursor="pointer"*/}
                                    {/*    _hover={{*/}
                                    {/*        bg: useColorModeValue('gray.50', 'gray.700')*/}
                                    {/*    }}*/}
                                    {/*    onClick={() => onSettingsChange?.('modelKnowledge', !settingsState.modelKnowledge)}*/}
                                    {/*>*/}
                                    {/*    <HStack justify="space-between" align="center">*/}
                                    {/*        <HStack spacing={2}>*/}
                                    {/*            <FiCpu size={14} color="gray.600" />*/}
                                    {/*            <Text fontSize="sm" fontWeight="500">Model knowledge</Text>*/}
                                    {/*        </HStack>*/}
                                    {/*        <Switch*/}
                                    {/*            size="sm"*/}
                                    {/*            colorScheme="blue"*/}
                                    {/*            isChecked={settingsState.modelKnowledge}*/}
                                    {/*            onChange={() => {}}*/}
                                    {/*            pointerEvents="none"*/}
                                    {/*        />*/}
                                    {/*    </HStack>*/}
                                    {/*</Box>*/}
                                </MenuList>
                            </Menu>
                        )}

                        {/* Large Context Toggle - For processing large document sets */}
                        {showSettings && (
                            <Tooltip
                                label={"Best for VERY LARGE document sets (slower but more thorough)"}
                                placement="top"
                                hasArrow
                                bg={'gray.700'}
                                color="white"
                                fontSize="xs"
                                px={3}
                                py={2}
                                borderRadius="md"
                            >
                                <Box
                                    px={3}
                                    py={2}
                                    cursor="pointer"
                                    _hover={{
                                        bg: 'gray.50'
                                    }}
                                    onClick={() => onSettingsChange?.('largeContext', !settingsState.largeContext)}
                                >
                                    <HStack justify="space-between" align="center">
                                        <Text fontSize="sm" fontWeight="500">Agent Mode</Text>
                                        <Switch
                                            size="sm"
                                            colorScheme="purple"
                                            isChecked={settingsState.largeContext}
                                            onChange={() => {}}
                                            pointerEvents="none"
                                        />
                                    </HStack>
                                </Box>
                            </Tooltip>
                        )}
                    </HStack>
                    
                    <HStack spacing={3}>
                        {/* Upload Menu */}
                        {showUploadMenu && (
                            <Menu placement="bottom-start">
                                <MenuButton
                                    as={IconButton}
                                    icon={<FiPlus size={16} />}
                                    aria-label="Add content"
                                    size="sm"
                                    bg={'gray.100'}
                                    color={'black'}
                                    h="28px"
                                    w="28px"
                                    minW="28px"
                                    borderRadius="md"
                                    _hover={{
                                        bg: 'gray.200'
                                    }}
                                    _active={{
                                        bg: 'gray.200'
                                    }}
                                ></MenuButton>
                                <MenuList
                                    bg={'white'}
                                    border="1px"
                                    borderColor={'gray.200'}
                                    borderRadius="md"
                                    boxShadow="lg"
                                    py={1}
                                    minW="160px"
                                >
                                    <MenuItem
                                        fontSize="sm"
                                        py={2}
                                        px={3}
                                        icon={<FiUpload size={16} />}
                                        onClick={onUploadFile}
                                        _hover={{
                                            bg: 'gray.50'
                                        }}
                                    >
                                        Upload source
                                    </MenuItem>
                                    {/*<MenuItem*/}
                                    {/*    fontSize="sm"*/}
                                    {/*    py={2}*/}
                                    {/*    px={3}*/}
                                    {/*    icon={<FiFile size={16} />}*/}
                                    {/*    onClick={onCreateNote}*/}
                                    {/*    _hover={{*/}
                                    {/*        bg: useColorModeValue('gray.50', 'gray.700')*/}
                                    {/*    }}*/}
                                    {/*>*/}
                                    {/*    Create note*/}
                                    {/*</MenuItem>*/}
                                </MenuList>
                            </Menu>
                        )}
                        
                        {/* Send Button */}
                        <IconButton
                            type="submit"
                            aria-label="Send message"
                            icon={<FaArrowUp size={14} />}
                            isDisabled={isDisabled || !value.trim() || isStreaming}
                            bg={useColorModeValue('blue.500', 'blue.600')}
                            color="white"
                            borderRadius="full"
                            size="sm"
                            w="28px"
                            h="28px"
                            minW="28px"
                            _hover={{
                                bg: useColorModeValue('blue.600', 'blue.700')
                            }}
                            _disabled={{
                                bg: useColorModeValue('gray.300', 'gray.600'),
                                cursor: 'not-allowed'
                            }}
                            transition="all 0.2s"
                        />
                    </HStack>
                </HStack>
            </Box>
        </form>
    );
};

